import EventPhaseCompletion from '@/model/EventPhaseCompletion';
import React, { useEffect, useState } from 'react';
import { States } from '@/helpers/common/states';
import { useParams } from 'react-router-dom';
import api, { AxiosError } from '@/services/api';
import { SectionPos } from '@/model/SectionPos';
import EventPos from '@/model/EventPos';
import Pos from '@/model/Pos';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import { useDialog } from '@/hooks/useDialog';
import { toast } from 'react-toastify';
import { useConfirmDelete } from '@/hooks/useConfirmDelete';
import { DeleteContent } from '@/components/DeleteContent';
import { FormInputName, ShouldShowModal } from '../types';
import { SectorPosContainer } from './ui';

interface StateProps {
  numberTab: number;
  phaseCompletion?: EventPhaseCompletion;
}

interface DispatchProps {
  nextTab: () => void;
  backTab: () => void;
  handleGetEventPhaseCompletion: () => void;
}

type Props = StateProps & DispatchProps;

type UrlParams = {
  id: string;
};

export const SectorPosScreen: React.FC<Props> = ({
  numberTab,
  phaseCompletion,
  handleGetEventPhaseCompletion,
  backTab,
}) => {
  const [state, setState] = useState<States>(States.default);
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.POS);
  const [allowPos, setAllowPos] = useState<boolean>(false);
  const [sections, setSections] = useState<SectionPos[]>([]);
  const [poss, setPoss] = useState<Pos[]>([]);

  const params = useParams<UrlParams>();
  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const confirmDelete = useConfirmDelete();

  const handleOnHasPos = async (value: boolean): Promise<void> => {
    try {
      setState(States.loading);
      await api.patch(`/event/section-product/${params.id}/pos/has/${value}`);
      setAllowPos(value);
      handleGetEventPhaseCompletion();
    } finally {
      setState(States.default);
    }
  };

  const handleOnGetSections = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<SectionPos[]>(`/event/section-product/${params.id}/pos`);
      setSections(data);
    } finally {
      setState(States.default);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      sectionId: '',
      pos: '',
      allowDiscount: 'false',
    },
    validators: {
      pos: [validators.required],
      allowDiscount: [validators.required],
    },
  });

  const handleOnGetPoss = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<Pos[]>('/pos/find');
      setPoss(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnShouldShowModal = async ({
    value,
    newTitleModal,
    sectionId,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    sectionId: string;
  }): Promise<void> => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onChangeFormInput(FormInputName.sectionId)(sectionId);
    await handleOnGetPoss();
    onToggle();
  };

  const handleOnSavePos = async (): Promise<void> => {
    if (isFormValid()) {
      try {
        setState(States.loading);
        const payload = {
          pos: { id: formData[FormInputName.pos] },
          allowDiscount: formData[FormInputName.allowDiscount] === 'true',
        } as EventPos;
        await api.post(
          `/event/section-product/${params.id}/pos/${formData[FormInputName.sectionId]}/section`,
          payload,
        );
        resetForm();
        handleOnGetSections();
        onToggle();
      } finally {
        setState(States.default);
      }
    }
  };

  const handleOnChangePosSwitch = async (sectionId: string, eventPos: EventPos): Promise<void> => {
    try {
      setState(States.loading);
      const payload = {
        pos: { id: eventPos.pos.id },
        allowDiscount: !eventPos.allowDiscount,
      } as EventPos;
      await api.post(`/event/section-product/${params.id}/pos/${sectionId}/section`, payload);
      handleOnGetSections();
    } finally {
      setState(States.default);
    }
  };

  const handleOnConfirmDeleteToPos = async (
    sectionId: string,
    eventPos: EventPos,
  ): Promise<void> => {
    try {
      setState(States.loading);
      await api.delete(
        `/event/section-product/${params?.id}/section/${sectionId}/pos/${eventPos.pos.id}`,
      );
      toast.success('Pos excluído com sucesso!');
      confirmDelete.hide();
      handleOnGetSections();
    } finally {
      setState(States.default);
    }
  };

  const handleOnShowDeletePos = (sectionId: string, eventPos: EventPos): void => {
    confirmDelete.show({
      title: '',
      children: <DeleteContent />,
      actions: [
        {
          title: 'Não, quero manter',
          theme: 'noneBorder',
          onClick: (): void => confirmDelete.hide(),
        },
        {
          title: 'Sim, quero excluir',
          onClick: (): Promise<void> => handleOnConfirmDeleteToPos(sectionId, eventPos),
        },
      ],
    });
  };

  useEffect(() => {
    handleOnGetSections();
  }, [allowPos]);

  useEffect(() => {
    if (phaseCompletion && phaseCompletion.sectionProduct && phaseCompletion.sectionProduct.pos) {
      setAllowPos(phaseCompletion.sectionProduct.pos);
    }
  }, [phaseCompletion]);

  useEffect(() => {
    if (numberTab === 4) {
      handleOnGetSections();
    }
  }, [numberTab]);

  return (
    <SectorPosContainer
      state={state}
      title={title}
      visible={visible}
      onToggle={onToggle}
      shouldShowModal={shouldShowModal}
      poss={poss}
      formData={formData}
      onChangeFormInput={onChangeFormInput}
      formErrors={formErrors}
      onSavePos={handleOnSavePos}
      allowPos={allowPos}
      onHasPos={handleOnHasPos}
      sections={sections}
      onChangePosSwitch={handleOnChangePosSwitch}
      onShouldShowModal={handleOnShouldShowModal}
      onShowDeletePos={handleOnShowDeletePos}
      backTab={backTab}
    />
  );
};
